import { useMemo, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { esCount } from '@/lib/es-count';
import { notify } from '@/lib/notify';
import { StoreSelect } from '../components/StoreSelect';
import {
    usePendingStoreBrands,
    useSetProductBrand,
    useStoreBrands,
    useUpdateStoreBrand,
    type StoreBrandRow,
} from './useStoreBrands';

function BrandEditor({ row, highlight }: { row: StoreBrandRow; highlight: boolean }) {
    const save = useUpdateStoreBrand();
    const [draft, setDraft] = useState(row);
    const [seen, setSeen] = useState(row.id + row.legalName + row.postalAddress + row.contactEmail);
    const next = row.id + row.legalName + row.postalAddress + row.contactEmail;
    if (seen !== next) {
        setSeen(next);
        setDraft(row);
    }
    const set = (key: keyof StoreBrandRow) => (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setDraft((prev) => ({ ...prev, [key]: value }));
    };
    return (
        <article
            id={`marca-${row.name}`}
            className={`space-y-3 rounded-xl border p-4 ${highlight ? 'border-primary' : 'border-border'}`}
        >
            <header className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold">{row.name}</h2>
                <p className="text-xs text-muted-foreground">{esCount(row.productCount, 'publicado')} · {row.supplierSlug}</p>
            </header>
            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                    <Label>Nombre legal</Label>
                    <Input value={draft.legalName} onChange={set('legalName')} />
                </div>
                <div className="space-y-1">
                    <Label>Email de contacto</Label>
                    <Input value={draft.contactEmail} onChange={set('contactEmail')} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                    <Label>Dirección postal</Label>
                    <Input value={draft.postalAddress} onChange={set('postalAddress')} />
                </div>
                <div className="space-y-1">
                    <Label>Responsable en la UE</Label>
                    <Input value={draft.euResponsibleName} onChange={set('euResponsibleName')} />
                </div>
                <div className="space-y-1">
                    <Label>Email del responsable</Label>
                    <Input value={draft.euResponsibleEmail} onChange={set('euResponsibleEmail')} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                    <Label>Dirección del responsable</Label>
                    <Input value={draft.euResponsibleAddress} onChange={set('euResponsibleAddress')} />
                </div>
            </div>
            <Button
                type="button"
                size="sm"
                disabled={save.isPending}
                onClick={() => {
                    save.mutate(draft, {
                        onSuccess: () => notify({ type: 'ok', text: `Datos de ${row.name} guardados` }),
                        onError: (error) => notify({ type: 'err', text: error.message }),
                    });
                }}
            >
                Guardar fabricante
            </Button>
        </article>
    );
}

export function StoreSettingsBrandsPage() {
    const brands = useStoreBrands();
    const pending = usePendingStoreBrands();
    const assign = useSetProductBrand();
    const [params] = useSearchParams();
    const focus = params.get('marca') ?? '';
    const names = useMemo(() => (brands.data ?? []).map((row) => row.name), [brands.data]);
    const [choice, setChoice] = useState<Record<number, string>>({});
    const [custom, setCustom] = useState<Record<number, string>>({});

    return (
        <div className="space-y-6">
            <section className="space-y-3">
                <h2 className="text-sm font-semibold">Marcas por asignar</h2>
                {(pending.data ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No hay productos en la cola.</p>
                ) : (
                    <ul className="space-y-3">
                        {(pending.data ?? []).map((row) => (
                            <li key={row.productId} className="space-y-2 rounded-xl border border-border p-4">
                                <p className="text-sm font-medium">{row.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    Texto del feed: {row.sourceText || 'vacío'} · {row.isActive ? 'publicado' : 'no publicado'}
                                </p>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    <StoreSelect
                                        ariaLabel={`Marca de ${row.name}`}
                                        value={choice[row.productId] ?? ''}
                                        placeholder="Elige una marca"
                                        onChange={(value) => {
                                            setChoice((prev) => ({ ...prev, [row.productId]: value }));
                                        }}
                                        options={[
                                            { value: '', label: 'Elige una marca' },
                                            ...names.map((name) => ({ value: name, label: name })),
                                            { value: '__otra', label: 'Otra marca' },
                                        ]}
                                    />
                                    {choice[row.productId] === '__otra' ? (
                                        <Input
                                            value={custom[row.productId] ?? ''}
                                            placeholder="Nombre de la marca"
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setCustom((prev) => ({ ...prev, [row.productId]: value }));
                                            }}
                                        />
                                    ) : null}
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    disabled={assign.isPending}
                                    onClick={() => {
                                        const selected = choice[row.productId] === '__otra'
                                            ? (custom[row.productId] ?? '').trim()
                                            : (choice[row.productId] ?? '').trim();
                                        if (!selected) {
                                            notify({ type: 'err', text: 'Elige o escribe la marca' });
                                            return;
                                        }
                                        assign.mutate(
                                            { productId: row.productId, brand: selected },
                                            {
                                                onSuccess: () => notify({ type: 'ok', text: 'Marca asignada' }),
                                                onError: (error) => notify({ type: 'err', text: error.message }),
                                            },
                                        );
                                    }}
                                >
                                    Asignar
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
            <section className="space-y-3">
                <h2 className="text-sm font-semibold">Datos del fabricante</h2>
                {(brands.data ?? []).map((row) => (
                    <BrandEditor key={row.id} row={row} highlight={focus !== '' && row.name === focus} />
                ))}
            </section>
        </div>
    );
}
